import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import StartEndTimeFields from '../../../../../ReusableFormFields/StartEndTimeFields'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { formatDatesToBE } from '../../formatters'
import { clearTimeZone, extractTimeStringFromDate } from '../../../../../../helpers/date'

import { clearUpdateLineItem, updateLineItem } from '../../../../../../modules/actions/lineItems'

import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { campaignSelector } from '../../../../../../modules/selectors/campaigns'

import { START_DATE, START_TIME, STOP_DATE, STOP_TIME } from '../../../../../Multiplatform/LineItemForms/fields'
import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'

const DatesSectionForm = ({ editItemData: lineItem, title, ...formProps }) => {
  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const { objective: campaignObjective } = useSelector(campaignSelector)

  const { time_start: timeStart, time_stop: timeStop } = lineItem

  const handleSubmit = useCallback(
    values => {
      const updateLineItemData = {
        account: selectedAdAccountId,
        campaign_objective: campaignObjective,
        ...formatDatesToBE(values[START_DATE], values[START_TIME], values[STOP_DATE], values[STOP_TIME]),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateLineItem(updateLineItemData, lineItem.id, FACEBOOK_PLATFORM))
    },
    [dispatch, lineItem.id, campaignObjective, selectedAdAccountId, title]
  )

  const initialValues = useMemo(
    () => ({
      [START_DATE]: clearTimeZone(timeStart) || '',
      [START_TIME]: (timeStart && extractTimeStringFromDate(clearTimeZone(timeStart))) || '',
      [STOP_DATE]: clearTimeZone(timeStop) || '',
      [STOP_TIME]: (timeStop && extractTimeStringFromDate(clearTimeZone(timeStop))) || ''
    }),
    [timeStart, timeStop]
  )

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <StartEndTimeFields formik={purifiedFormik} />
    </EditForm>
  )
}

export default DatesSectionForm
